import type React from 'react';
import { useContext, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import classnames from 'clsx';

import { CurrencyInput } from 'ui-components/components/currency-input';
import { Input, version2 } from 'ui-components/components/input';
import { FloatTextAreaWithCharacterCount } from '../../../../../components/float-textarea-with-character-count/float-textarea-with-character-count';
import { noop } from 'ui-components/default-props/noop';
import { ReportingCategoryDropdownV3 as ProductCategoryDropdown } from '../../general/reporting-category-dropdown-v3';
import { OrderTagsDropdown } from '../../general/order-tags-dropdown';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { Checkbox } from 'ui-components/components/checkbox';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import Global from '../../../../../store/global-selectors';

import { UpsellModal } from '../../../../../components/upsell-modal/upsell-modal';
import { useEnableBundledProducts } from '../../../../../feature-flags/hooks/use-enable-bundled-products';
import { useEnableLtlProductFields } from '../../../../../feature-flags/hooks/use-enable-ltl-product-fields';
import { useIsSerialNumberTrackingAvailable } from '../../../../../feature-gates/use-is-serial-number-tracking-available';
import { useModal } from '../../../../../hooks/use-modal';
import { ProductBundles } from '../../../../../constants/features';
import { UpsellBanner } from '../../general/bundles/bundle-component-table/upsell-banner';
import { ProductVariants } from '../../general/product-variants';
import { useProductDetailsContext } from '../../../use-product-details-context';
import { BundleContext } from '../../../bundle-context';
import { ProductTypeDropdown } from '../../product-type-dropdown';
import { SaveInPlace } from '../../general/save-in-place';
import { ProductTypes } from '../../../../../enums/product-types';
import { ProductDetailsBundle } from '../../general/bundles';
import { InheritedTags } from '../../general/inherited-tags';
import { ShipStationFulfillmentSettings } from '../../general/shipstation-fulfillment-settings';
import { useIsSellerOnFeatureGatedPlan } from '../../../../../feature-gates/use-is-seller-on-feature-gated-plan';
import { getSubscriptionPath } from '../../../../../routes/constants';
import { useAdvancedProductDisplay } from '../../../../../hooks/use-seller-setting';

import { messages } from '../../../messages';
import { messages as generalMessages } from '../../general/messages';
import sharedStyle from '../../../style.module.css';
import style from './general.module.css';

const DESCRIPTION_MAX_LENGTH = 5000;

export const ProductDetailsGeneralTabV3 = () => {
  const navigate = useNavigate();
  const {
    userHasPermission,
    productName,
    setProductName,
    productSku,
    setProductSku,
    isActive,
    setIsActive,
    description,
    setDescription,
    imageUrl,
    setImageUrl,
    upc,
    setUpc,
    price,
    setPrice,
    cost,
    setCost,
    isReturnable,
    setIsReturnable,
    shouldOverrideImportedName,
    setShouldOverrideImportedName,
    requireSerialNumber,
    setRequireSerialNumber,
    productType,
    productId,
    isSaveInPlaceRequired,
    parentProduct,
    isShipStationFulfillmentEnabled,
    closeModal,
    validationErrors,
    hasAttemptedSave,
    touchedFields,
    markFieldTouched,
  } = useProductDetailsContext();
  const { isBundle, setIsBundle } = useContext(BundleContext);
  const intl = useIntl();
  const currencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const isNewProductFieldsLtlEnabled = useEnableLtlProductFields();
  const isNew = !productId;
  const showVariants = productType === ProductTypes.Parent.name && !isNew;
  const showInheritedTags = productType === ProductTypes.Variant.name;
  const { open, isOpen: isBundlesModalOpen, close } = useModal();
  const hasAdvancedProductDisplay = useAdvancedProductDisplay();
  const productTypeDisplayText = (ProductTypes[productType as keyof typeof ProductTypes] ?? ProductTypes.Standalone).name;

  const shouldShowNameError = (touchedFields.includes('productName') || hasAttemptedSave) && validationErrors?.productName;
  const shouldShowSkuError = (touchedFields.includes('productSku') || hasAttemptedSave) && validationErrors?.productSku;
  const shouldShowUpcError = (touchedFields.includes('upc') || hasAttemptedSave) && validationErrors?.upc;

  const nameErrorText = useMemo(
    () => (shouldShowNameError ? <Text isBold textType={TextType.Caption} value={validationErrors?.productName?.message} /> : undefined),
    [shouldShowNameError, validationErrors?.productName?.message],
  );
  const skuErrorText = useMemo(
    () => (shouldShowSkuError ? <Text isBold textType={TextType.Caption} value={validationErrors?.productSku?.message} /> : undefined),
    [shouldShowSkuError, validationErrors?.productSku?.message],
  );
  const upcErrorText = useMemo(
    () => (shouldShowUpcError ? <Text isBold textType={TextType.Caption} value={validationErrors?.upc?.message} /> : undefined),
    [shouldShowUpcError, validationErrors?.upc?.message],
  );

  const onNameChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setProductName(event.target.value);
    },
    [setProductName],
  );

  const onSkuChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setProductSku(event.target.value);
    },
    [setProductSku],
  );

  const onPriceChanged = useCallback(
    (value: unknown) => {
      setPrice(value);
    },
    [setPrice],
  );

  const onCostChanged = useCallback(
    (value: unknown) => {
      setCost(value);
    },
    [setCost],
  );

  const onUpcChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUpc(event.target.value);
    },
    [setUpc],
  );

  const onFieldBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      markFieldTouched(event.target.name);
    },
    [markFieldTouched],
  );

  const onOverrideImportedNameChanged = useCallback(
    (isChecked: boolean) => {
      setShouldOverrideImportedName(isChecked);
    },
    [setShouldOverrideImportedName],
  );

  const hasBundlesEnabled = useEnableBundledProducts();
  const { isAvailable: isSerialNumberTrackingAvailable } = useIsSerialNumberTrackingAvailable();
  const isOnGoodBetterBestPlanProfile = useIsSellerOnFeatureGatedPlan();

  const handleUpsellBannerClick = useCallback(() => {
    if (isOnGoodBetterBestPlanProfile) {
      closeModal?.();
      navigate(getSubscriptionPath());
    } else {
      open();
    }
  }, [isOnGoodBetterBestPlanProfile, closeModal, navigate, open]);

  const renderBundleConfig = useCallback(() => {
    const subheaderMessage = isOnGoodBetterBestPlanProfile
      ? generalMessages.upgradeToStandardOrPremiumDescription
      : generalMessages.upsellDescription;

    return (
      <>
        {!hasBundlesEnabled && (
          <>
            <UpsellBanner buttonMessage={generalMessages.upgrade} onOpen={handleUpsellBannerClick} subheaderMessage={subheaderMessage} />
            {isBundlesModalOpen && <UpsellModal onClose={close} onError={noop} upsellModalContentCode={ProductBundles} withContext />}
          </>
        )}
        <div
          className={classnames(sharedStyle.isBundleToggle, {
            [sharedStyle.hasBundlesDisabled]: !hasBundlesEnabled,
          })}
        >
          <CheckboxSlider
            checkboxId={`${productId}-bundle`}
            checked={isBundle}
            disabled={!userHasPermission || !hasBundlesEnabled}
            labelText={intl.formatMessage(messages.productIsBundleLabelV3)}
            onChange={setIsBundle}
          />
        </div>
      </>
    );
  }, [
    hasBundlesEnabled,
    handleUpsellBannerClick,
    isBundlesModalOpen,
    close,
    productId,
    isBundle,
    userHasPermission,
    intl,
    setIsBundle,
    isOnGoodBetterBestPlanProfile,
  ]);

  return (
    <div className={style['general-tab']}>
      {/* === MAIN CONTENT CARD (Overview + Attributes) === */}
      <div className={style['content-card']}>
        {/* === OVERVIEW SECTION === */}
        <div className={style.section}>
          <Text element='div' textType={TextType.H4} value={messages.overviewSectionTitle} />

          <div className={style['product-type-row']}>
            <Text className={style.label} element='div' textType={TextType.Label} value={messages.productType} />
            {isNew && hasAdvancedProductDisplay ? (
              <ProductTypeDropdown />
            ) : (
              <Text element='div' textType={TextType.Paragraph} value={productTypeDisplayText} />
            )}
          </div>

          <FlexRow className={style['name-sku-row']} gap={Gap.Space16}>
            <div className={style['name-field']}>
              <Text className={style.label} element='div' textType={TextType.Label} value={messages.productName} />
              <Input
                className={style['rounded-input']}
                data-testid='product-name'
                disabled={!userHasPermission}
                inlineMessage={nameErrorText}
                inlineMessageType={nameErrorText ? 'error' : undefined}
                name='productName'
                onBlur={onFieldBlur}
                onChange={onNameChanged}
                value={productName}
                version={version2}
              />
            </div>
            <div className={style['sku-field']}>
              <Text className={style.label} element='div' textType={TextType.Label} value={messages.productSku} />
              <Input
                className={style['rounded-input']}
                data-testid='product-sku'
                disabled={!isNew || !userHasPermission}
                inlineMessage={skuErrorText}
                inlineMessageType={skuErrorText ? 'error' : undefined}
                name='productSku'
                onBlur={onFieldBlur}
                onChange={onSkuChanged}
                value={productSku}
                version={version2}
              />
            </div>
          </FlexRow>

          <div className={style['override-row']}>
            <Checkbox
              checked={shouldOverrideImportedName}
              disabled={!userHasPermission}
              label={intl.formatMessage(messages.overrideImportedNameLabel)}
              onChange={onOverrideImportedNameChanged}
            />
          </div>

          <FlexRow className={style['toggles-row']} gap={Gap.Space16}>
            <FlexColumn className={style['toggle-column']} gap={Gap.Space12}>
              <CheckboxSlider
                checkboxId={`${productId}-active`}
                checked={isActive}
                disabled={!userHasPermission}
                labelText={intl.formatMessage(messages.productIsActiveLabel)}
                onChange={setIsActive}
              />
            </FlexColumn>
            <FlexColumn className={style['toggle-column']} gap={Gap.Space12}>
              <CheckboxSlider
                checkboxId={`${productId}-returnable`}
                checked={isReturnable}
                disabled={!userHasPermission}
                labelText={intl.formatMessage(messages.productIsReturnableLabel)}
                onChange={setIsReturnable}
              />
            </FlexColumn>
          </FlexRow>

          {isSerialNumberTrackingAvailable ? (
            <FlexRow className={style['toggles-row']} gap={Gap.Space16}>
              <FlexColumn className={style['toggle-column']} gap={Gap.Space4}>
                <CheckboxSlider
                  checkboxId={`${productId}-require-serial-number`}
                  checked={requireSerialNumber}
                  disabled={!userHasPermission}
                  labelText={intl.formatMessage(messages.requireSerialNumberLabel)}
                  onChange={setRequireSerialNumber}
                />
                <Text className={style['helper']} element='div' textType={TextType.Caption} value={messages.requireSerialNumberCaption} />
              </FlexColumn>
            </FlexRow>
          ) : null}
        </div>

        {/* === ATTRIBUTES SECTION === */}
        <div className={style.section}>
          <Text element='div' textType={TextType.H4} value={messages.generalTitle} />

          <div className={style['attributes-grid']}>
            <div className={style['attributes-left']}>
              <div>
                <Text className={style.label} element='div' textType={TextType.Label} value={messages.orderTags} />
                <OrderTagsDropdown />
              </div>
              {showInheritedTags && (
                <div>
                  <Text className={style.label} element='div' textType={TextType.Label} value={messages.inheritedTags} />
                  <InheritedTags tagIds={parentProduct?.tags ?? []} />
                </div>
              )}
              <div>
                <Text
                  className={style.label}
                  element='div'
                  textType={TextType.Label}
                  value={isNewProductFieldsLtlEnabled ? messages.barcode : messages.upc}
                />
                <Input
                  data-testid='upc'
                  disabled={!userHasPermission}
                  inlineMessage={upcErrorText}
                  inlineMessageType={upcErrorText ? 'error' : undefined}
                  name='upc'
                  onBlur={onFieldBlur}
                  onChange={onUpcChanged}
                  value={upc}
                  version={version2}
                />
              </div>
              <div>
                <Text className={style.label} element='div' textType={TextType.Label} value={messages.productCategories} />
                <ProductCategoryDropdown />
              </div>
              {isNewProductFieldsLtlEnabled && (
                <FlexRow gap={Gap.Space16}>
                  <div className={style['half-field']}>
                    <Text className={style.label} element='div' textType={TextType.Label} value={messages.price} />
                    <CurrencyInput
                      currencyCode={currencyCode}
                      data-testid='price'
                      disabled={!userHasPermission}
                      onChange={onPriceChanged}
                      value={price ?? ''}
                    />
                  </div>
                  <div className={style['half-field']}>
                    <Text className={style.label} element='div' textType={TextType.Label} value={messages.cost} />
                    <CurrencyInput
                      currencyCode={currencyCode}
                      data-testid='cost'
                      disabled={!userHasPermission}
                      onChange={onCostChanged}
                      value={cost ?? ''}
                    />
                  </div>
                </FlexRow>
              )}
            </div>
            <div className={style['attributes-right']}>
              <Text className={style.label} element='div' textType={TextType.Label} value={messages.description} />
              <FloatTextAreaWithCharacterCount
                data-testid='description'
                disabled={!userHasPermission}
                max={DESCRIPTION_MAX_LENGTH}
                name='description'
                onChange={setDescription}
                value={description}
              />
            </div>
          </div>

          {/* Image */}
          <div className={style['image-block']}>
            <Text className={style.label} element='div' textType={TextType.Label} value={messages.imageSectionTitle} />
            <Input
              className={style['image-url-input']}
              data-testid='thumbnail-url'
              disabled={!userHasPermission}
              onChange={setImageUrl}
              placeholder={intl.formatMessage(messages.imageUrl)}
              value={imageUrl}
            />
            <Text className={style.helper} element='div' textType={TextType.Caption} value={messages.imageUrlDescription} />
          </div>
        </div>
      </div>

      {/* === BUNDLE TOGGLE CARD === */}
      <div className={style['toggle-card']}>
        {renderBundleConfig()}
        {hasBundlesEnabled && isBundle && <ProductDetailsBundle />}
      </div>

      {/* === SAVE IN PLACE TOGGLE CARD === */}
      {isSaveInPlaceRequired && (
        <div className={style['toggle-card']}>
          <SaveInPlace />
        </div>
      )}

      {/* === VARIANTS TOGGLE CARD === */}
      {showVariants && (
        <div className={style['toggle-card']}>
          <ProductVariants />
        </div>
      )}

      {/* === FULFILLMENT SETTINGS TOGGLE CARD === */}
      {isShipStationFulfillmentEnabled && (
        <div className={classnames(style['toggle-card'], style['toggle-card--fulfillment'])}>
          <ShipStationFulfillmentSettings />
        </div>
      )}
    </div>
  );
};
